import { useNavigate } from 'react-router';
import { useMutation } from '@tanstack/react-query';

import { clearAuthSession } from 'api/clearAuthSession';
import { addNotification } from 'notifications';
import { setRole, setUserRoleIds } from 'api/tokenStorage';
import { api, type ApiRequestConfig } from 'api/v1/api';
import { applyAuthSession } from 'hooks/useAuthSession';
import { getApiErrorMessage, getApiSuccessMessage } from 'utils/getApiErrorMessage';
import { resolvePostLoginNavigation } from 'utils/postLoginNavigation';
import { consumeReturnTo, resolveAfterStudentRegistrationPath } from 'utils/returnTo';
import { METRIKA_GOALS, reachMetrikaGoal } from 'utils/metrikaGoals';
import { readPodborDraft, saveVerificationBrowserKey } from 'utils/podborDraft';

export interface RegisterFlowRole {
  id: number;
  title: string;
}

export interface RegisterFlowResponse {
  requires_password_confirmation?: boolean;
  existing_roles?: RegisterFlowRole[];
  role_added_to_existing_account?: boolean;
  token?: string;
  roles?: number[];
  role_id?: number;
  message?: string;
  email_sent?: boolean;
  verification_browser_key?: string;
  phone_otp_required?: boolean;
  phone_login_required?: boolean;
  has_password?: boolean;
  challenge_id?: string;
  channel?: string;
  cascade_after?: number;
  otp_error?: string;
  phone?: string;
  /** Present when the register call started a session (second role on existing account). */
  id?: number;
  email?: string | null;
  email_verified?: boolean;
  phone_verified?: boolean;
}

export interface RegisterFlowValues {
  method?: 'email' | 'phone';
  email?: string;
  phone?: string;
  password?: string;
  role: string;
  studentRegistrantType?: string;
  name: string;
  surname?: string;
  patronymic?: string;
  gender: string;
  birthDate?: string;
}

function trackStudentParentRegistration(values: RegisterFlowValues): void {
  if (values.role !== 'student') return;
  const fromDraft = readPodborDraft()?.registrantType;
  const isParent =
    fromDraft === 'parent' ||
    (fromDraft !== 'self' && values.studentRegistrantType === 'parent');
  reachMetrikaGoal(
    isParent ? METRIKA_GOALS.registrationParent : METRIKA_GOALS.registrationStudent,
  );
}

export const useRegisterFlow = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (values: RegisterFlowValues) => {
      const roleId = values.role === 'teacher' ? 1 : 2;
      const sex = values.gender ? (values.gender === 'female' ? 0 : 1) : null;
      const childName = String((values as { childName?: string }).childName || '').trim();
      const childGender = String((values as { childGender?: string }).childGender || '');
      const podborDraft = values.role === 'student' ? readPodborDraft() : null;
      const registrantFromDraft =
        podborDraft?.registrantType === 'parent' || podborDraft?.registrantType === 'self'
          ? podborDraft.registrantType
          : undefined;
      const payload: Record<string, unknown> = {
        roles: [roleId],
        student_registrant_type:
          values.role === 'student'
            ? registrantFromDraft || values.studentRegistrantType || 'self'
            : undefined,
        podbor_draft: podborDraft || undefined,
        profile: {
          name: values.name,
          surname: values.role === 'teacher' ? values.surname?.trim() || null : null,
          date_of_birth: values.role === 'teacher' ? values.birthDate || null : null,
          sex,
          ...(values.role === 'student' && childName
            ? { child_name: childName, child_sex: childGender ? (childGender === 'female' ? 0 : 1) : null }
            : {}),
          ...(values.role === 'teacher'
            ? { patronymic: values.patronymic?.trim() || null }
            : {}),
        },
      };
      if (values.method === 'phone' || (!values.email && values.phone)) {
        payload.phone = values.phone;
      } else {
        payload.email = values.email;
        payload.password = values.password;
      }

      await clearAuthSession();

      const response = await api.post('/register/', payload, {
        skipAuthRefresh: true,
        skipGlobalErrorHandler: true,
        skipAuthHeader: true,
      } as ApiRequestConfig);

      localStorage.setItem('registration_email', values.email || '');
      localStorage.setItem('registration_role', values.role);
      if (values.role === 'student') {
        localStorage.setItem(
          'registration_student_type',
          registrantFromDraft || values.studentRegistrantType || 'self',
        );
      } else {
        localStorage.removeItem('registration_student_type');
      }
      if (response.data.verification_browser_key) {
        saveVerificationBrowserKey(response.data.verification_browser_key);
      }

      const data = response.data as RegisterFlowResponse;
      if (!data?.requires_password_confirmation) {
        trackStudentParentRegistration(values);
      }

      return data;
    },
    onSuccess: (data) => {
      if (data?.requires_password_confirmation) {
        addNotification(
          getApiSuccessMessage(data) ||
            'Аккаунт уже существует. Введите существующий пароль, чтобы добавить вторую роль.',
          'info',
        );
        return;
      }

      if (data?.role_added_to_existing_account) {
        const studentType = localStorage.getItem('registration_student_type');
        const registrationRole = localStorage.getItem('registration_role');
        const returnTo = consumeReturnTo();
        const roles = data.roles ?? [];
        const isStudentReg = registrationRole === 'student';
        const nav = isStudentReg
          ? null
          : resolvePostLoginNavigation(roles, returnTo);
        const path = isStudentReg
          ? resolveAfterStudentRegistrationPath(studentType, returnTo)
          : nav!.path;
        const preferredRoleId = isStudentReg ? 2 : nav!.preferredRoleId;
        setRole(roles, preferredRoleId);
        setUserRoleIds(roles);
        applyAuthSession({ ...data, id: data.id as number });
        localStorage.removeItem('registration_student_type');
        addNotification(
          getApiSuccessMessage(data) || 'Роль добавлена. Вы вошли в аккаунт.',
          'success',
        );
        navigate(path, { replace: true });
        return;
      }

      if (data?.phone_otp_required) {
        // Экран «Подтвердите телефон» (21:10034) сам показывает заголовок, способ и ошибку
        // отправки (otp_error), поэтому всплывающее уведомление здесь было бы дублем.
        return;
      }

      if (data?.phone_login_required) {
        // Текст «номер уже зарегистрирован» и «Войти» показывает сама форма регистрации - без дубля тостом.
        return;
      }

      if (data?.message) {
        addNotification(getApiSuccessMessage(data), data.email_sent ? 'success' : 'info');
      }
      navigate('/registration/confirm');
    },
    onError: (error) => {
      addNotification(getApiErrorMessage(error, 'Не удалось зарегистрироваться'), 'error');
    },
  });
};
